import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SanityImage from 'gatsby-plugin-sanity-image';
import breakpoints from '../../breakpoints';
import sharedStyles from '../../styles';

const Container = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-block: ${sharedStyles.spacing['spacing-1xl']};
`;

const Background = styled.div`
  width: 100%;

  img {
    width: 100%;
    height: auto;
    object-fit: cover;

    ${({ hotspot }) =>
      hotspot &&
      `
      aspect-ratio: 3/2;

      @media (min-width: ${breakpoints.lg}) {
        aspect-ratio: 3/1;
      }

      @media (min-width: ${breakpoints.xl}) {
        aspect-ratio: 42/9;
      }
    `}
  }
`;

const Overlay = styled.div`
  position: relative;
  display: grid;
  gap: ${sharedStyles.spacing['spacing-1xl']};
  grid-template-columns: repeat(1, 1fr);
  max-width: ${breakpoints.xl};
  margin: ${sharedStyles.spacing['spacing-md']} 0
    ${sharedStyles.spacing['spacing-1xl']};
  margin-block: ${sharedStyles.spacing['spacing-1xl']} auto;
  padding: 0 ${sharedStyles.spacing['spacing-lg']};

  @media screen and (min-width: ${breakpoints.md}) {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    color: ${({ theme }) => theme.hero.fontColor};
    text-shadow: 3px 3px 7px rgb(0 0 0 / 35%);
    margin: 0 auto;
    margin-block: 0;
    grid-template-columns: repeat(2, 1fr);

    .last-column {
      ${({ columnCount }) =>
        columnCount % 2 &&
        `
          grid-column: 1 / 3;
      `};
    }
  }

  @media screen and (min-width: ${breakpoints.lg}) {
    grid-template-columns: ${({ columnCount }) =>
      `repeat(${columnCount || 1}, 1fr)`};

    .last-column {
      grid-column-start: ${({ columnCount }) => columnCount};
      grid-column-end: ${({ columnCount }) => columnCount};
    }
  }
`;

const Column = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  flex-wrap: wrap;
  text-align: center;
  line-height: 1.15;
`;

const EmphasisText = styled.span`
  display: block;
  font-size: ${sharedStyles.fontSize['5xl']};
  font-weight: ${sharedStyles.fontWeight.semiBold};
`;

const Text = styled.span`
  display: block;
  font-size: ${sharedStyles.fontSize['3xl']};
`;

function BlockBuilderImageBanner({ value }) {
  return (
    value?.media?.asset && (
      <Container className="blockbuilder-image-banner">
        <Background hotspot={value?.media?.hotspot}>
          <SanityImage
            asset={value.media.asset}
            hotspot={value?.media?.hotspot}
            crop={value?.media?.crop}
            alt={value?.media?.alt || ''}
          />
        </Background>
        <Overlay columnCount={value?.textColumns?.length}>
          {value?.textColumns?.length > 0 &&
            value.textColumns.map((item, index) => (
              <Column
                // eslint-disable-next-line no-underscore-dangle
                key={item._key}
                className={
                  value.textColumns.length === index + 1 && 'last-column'
                }
              >
                <div>
                  {item?.emphasisText && (
                    <EmphasisText>{item.emphasisText}</EmphasisText>
                  )}
                  {item?.text && <Text>{item.text}</Text>}
                </div>
              </Column>
            ))}
        </Overlay>
      </Container>
    )
  );
}

BlockBuilderImageBanner.propTypes = {
  value: PropTypes.shape({
    title: PropTypes.string,
    media: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
      alt: PropTypes.string,
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
    }),
    textColumns: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default BlockBuilderImageBanner;
